import styled from "styled-components"
import colors from "~constants/colors"
import { rgba } from "polished"
import UserDetailActions from "./UserDetailActions"
import { NotificationType } from "~constants/notifications"
import { UserShape } from "~shapes/user"
import { isUserActive } from "~components/companies/utils/user"
import moment from "moment"
import SkeletonWrapper from "~components/SkeletonWrapper"
import { CircularProgress } from "@mui/material"

interface Props {
  user: UserShape
  isLoading: boolean
  isSendingNotification: NotificationType | null
  onEdit: () => void
  onDelete: () => void
  onSendNotification: (v) => void
}

const UserDetailHeader = ({
  user,
  isLoading,
  isSendingNotification,
  onEdit,
  onDelete,
  onSendNotification,
}: Props) => {
  const status = user && isUserActive(user) ? "Active" : "Inactive"
  const sinceRaw = user?.profile?.createdAt || user?.createdAt
  const since = sinceRaw ? moment(sinceRaw).format("MMM D, YYYY") : "N/A"

  return (
    <HeaderWrapper>
      {isLoading ? (
        <LoadingState>
          <CircularProgress size={18} />
          <LoadingText>Loading user details...</LoadingText>
        </LoadingState>
      ) : (
        <>
          <TopRow>
            <InfoGrid>
              <InfoItem>
                <InfoLabel>Email</InfoLabel>
                <InfoValue>
                  <SkeletonWrapper isLoading={isLoading} width={200}>
                    <span>{user?.email || "N/A"}</span>
                  </SkeletonWrapper>
                </InfoValue>
              </InfoItem>

              <InfoItem>
                <InfoLabel>Name</InfoLabel>
                <InfoValue>
                  <SkeletonWrapper isLoading={isLoading} width={140}>
                    <span>{user?.profile?.name || "N/A"}</span>
                  </SkeletonWrapper>
                </InfoValue>
              </InfoItem>

              <InfoItem>
                <InfoLabel>Since</InfoLabel>
                <InfoValue>
                  <SkeletonWrapper isLoading={isLoading} width={90}>
                    <span>{since}</span>
                  </SkeletonWrapper>
                </InfoValue>
              </InfoItem>
            </InfoGrid>

            <TopActions>
              <SkeletonWrapper isLoading={isLoading} width={66} height={24}>
                <StatusChip $active={status === "Active"}>{status}</StatusChip>
              </SkeletonWrapper>
              <UserDetailActions onEdit={onEdit} onDelete={onDelete} compact />
            </TopActions>
          </TopRow>

          <ActionRow>
            <ActionButton
              $variant="primary"
              onClick={() =>
                onSendNotification(NotificationType.WEB_LOGIN_LINK)
              }
              disabled={isSendingNotification !== null || !user}
            >
              {isSendingNotification === NotificationType.WEB_LOGIN_LINK
                ? "Sending..."
                : "Send Web Login Link"}
            </ActionButton>

            <ActionButton
              $variant="secondary"
              onClick={() =>
                onSendNotification(NotificationType.WEB_RESET_PASSWORD_LINK)
              }
              disabled={isSendingNotification !== null || !user}
            >
              {isSendingNotification ===
              NotificationType.WEB_RESET_PASSWORD_LINK
                ? "Sending..."
                : "Send Reset Password Link"}
            </ActionButton>
          </ActionRow>
        </>
      )}
    </HeaderWrapper>
  )
}

const HeaderWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const TopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(120px, auto));
  gap: 8px 16px;
  align-items: center;
`

const InfoItem = styled.div`
  min-width: 0;
`

const InfoLabel = styled.div`
  font-size: 10px;
  letter-spacing: 0.35px;
  text-transform: uppercase;
  color: ${colors.gray7};
  margin-bottom: 2px;
`

const InfoValue = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: ${colors.gray3};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const TopActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const StatusChip = styled.span<{ $active: boolean }>`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  color: ${colors.white};
  background: ${({ $active }) => ($active ? colors.green : colors.gray6)};
  white-space: nowrap;
`

const ActionRow = styled.div`
  display: flex;
  gap: 6px;
  justify-content: flex-end;
  padding-top: 6px;
  border-top: 1px solid ${colors.gray2};
  flex-wrap: wrap;
`

const ActionButton = styled.button<{ $variant: "primary" | "secondary" }>`
  height: 32px;
  padding: 0 12px;
  font-family: inherit;
  font-size: 12px;
  font-weight: 600;
  border-radius: 7px;
  border: 1px solid
    ${({ $variant }) =>
      $variant === "primary" ? rgba(colors.blue1, 0.28) : colors.gray2};
  background: ${({ $variant }) =>
    $variant === "primary"
      ? rgba(colors.blue2, 0.15)
      : rgba(colors.gray2, 0.4)};
  color: ${({ $variant }) =>
    $variant === "primary" ? colors.blue1 : colors.gray8};
  cursor: pointer;
  white-space: nowrap;
  transition:
    background 0.15s ease,
    border-color 0.15s ease;

  &:hover:not(:disabled) {
    background: ${({ $variant }) =>
      $variant === "primary" ? rgba(colors.blue2, 0.22) : colors.gray1};
    border-color: ${({ $variant }) =>
      $variant === "primary" ? rgba(colors.blue1, 0.4) : colors.gray4};
  }

  &:focus-visible {
    outline: 2px solid ${colors.blue1};
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const LoadingState = styled.div`
  min-height: 72px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
`

const LoadingText = styled.span`
  font-size: 13px;
  color: ${colors.gray6};
`

export default UserDetailHeader
